import { Box, Stack, Tab, Tabs, Typography } from '@mui/material';
import { ApplicationShort, ApplicationStatus, useGetApplicationsQuery } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { PageHeader, PageHeaderSearchInput } from '@schooly/components/filters';
import { MainGridNoResultsStub } from '@schooly/components/stubs';
import {
  Counter,
  EmptyFolderSvg,
  GridBody,
  MainPageGrid,
  PlusIcon,
  SkeletonGridLoader,
  SkeletonRows,
} from '@schooly/style';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import useUserCounts from '../../hooks/useUserCounts';
import { ApplicationRow, ApplicationsHeader, SKELETON_COLS } from './ApplicationsGrid';

export enum ApplicationsTab {
  Open,
  Converted,
  Rejected,
}

const selectedApplicationTabParams: { [key in ApplicationsTab]: ApplicationStatus } = {
  [ApplicationsTab.Open]: 'open',
  [ApplicationsTab.Converted]: 'converted',
  [ApplicationsTab.Rejected]: 'rejected',
};

const statusLabelIds: { [key in ApplicationsTab]: string } = {
  [ApplicationsTab.Open]: 'applications-Status-Open',
  [ApplicationsTab.Converted]: 'applications-Status-Converted',
  [ApplicationsTab.Rejected]: 'applications-Status-Rejected',
};

const PAGE_SIZE = 20;

export const ApplicationsContent = () => {
  const { schoolId = '', permissions } = useAuth();

  const { formatMessage } = useIntl();
  const [activeTab, setActiveTab] = useState<ApplicationsTab>(ApplicationsTab.Open);
  const navigate = useNavigate();

  const { userCounts } = useUserCounts();
  const canCreateApplication = permissions.includes('application_manager');
  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage, params, setParams } =
    useGetApplicationsQuery(
      {
        school_id: schoolId,
        status: selectedApplicationTabParams[activeTab],
        page_size: PAGE_SIZE,
      },
      { refetchOnMount: 'always', enabled: !!schoolId },
    );

  const entries = useMemo(
    () =>
      data?.pages.reduce<ApplicationShort[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setParams((params) => ({
      ...params,
      status: selectedApplicationTabParams[newValue as ApplicationsTab],
    }));
  };

  const handleQueryChange = useCallback(
    (query: string) =>
      setParams({
        ...params,
        query,
      }),
    [setParams, params],
  );

  const total = data?.pages[0].count;

  const renderTabs = () => {
    return (
      <Tabs sx={{ height: 36, minHeight: 36 }} value={activeTab} onChange={handleTabChange}>
        <Tab
          label={
            <Stack direction="row" alignItems="center">
              {formatMessage({ id: 'applications-Status-Open' })}
              {!!userCounts?.application && (
                <Counter
                  sx={(theme) => ({
                    backgroundColor: 'success.main',
                    px: 0.5,
                    minWidth: theme.spacing(2),
                    height: theme.spacing(2),
                  })}
                >
                  <Typography sx={{ verticalAlign: 'top' }} variant="body3">
                    {userCounts.application}
                  </Typography>
                </Counter>
              )}
            </Stack>
          }
        />
        <Tab label={formatMessage({ id: 'applications-Status-Converted' })} />
        <Tab label={formatMessage({ id: 'applications-Status-Rejected' })} />
      </Tabs>
    );
  };

  if (data && !params.query && !total)
    return (
      <Stack gap={1} flex={1}>
        <PageHeader
          pageTitleTextId="applications-Title-plural"
          buttonTextId="applications-New"
          showActionButton={canCreateApplication}
          buttonIcon={<PlusIcon />}
          onButtonClick={() => navigate('/applications/new')}
        >
          <PageHeaderSearchInput
            value=""
            onChangeText={() => {}}
            disabled
            placeholder={formatMessage({ id: 'people-Search' })}
          />
        </PageHeader>

        {renderTabs()}
        <Stack alignItems="center" justifyContent="center" flex={1}>
          <Box width={300}>
            <Box sx={{ mb: 2, '& svg': { width: 300, height: 300 } }}>
              <EmptyFolderSvg />
            </Box>
            <Typography variant="h3" align="center" color="text.primary">
              <FormattedMessage
                id="applications-EmptyList"
                values={{
                  applicationStatus: formatMessage({ id: statusLabelIds[activeTab] }).toLowerCase(),
                }}
              />
            </Typography>
          </Box>
        </Stack>
      </Stack>
    );

  return (
    <>
      <Stack gap={1}>
        <PageHeader
          pageTitleTextId="applications-Title-plural"
          buttonTextId="applications-New"
          showActionButton={canCreateApplication}
          buttonIcon={<PlusIcon />}
          onButtonClick={() => navigate('/applications/new')}
        >
          <PageHeaderSearchInput
            value={params.query || ''}
            onChangeText={handleQueryChange}
            placeholder={formatMessage({ id: 'people-Search' })}
          />
        </PageHeader>
        {renderTabs()}
      </Stack>

      <>
        <MainPageGrid mt={2.5}>
          <ApplicationsHeader />
          <GridBody key={activeTab}>
            {entries?.map((entry) => (
              <ApplicationRow application={entry} key={entry.id} />
            ))}
            {isLoading && <SkeletonRows columnsCount={SKELETON_COLS} amount={PAGE_SIZE} />}
            <SkeletonGridLoader
              isFetching={isLoading || isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              columnsCount={SKELETON_COLS}
              amount={Math.min(
                PAGE_SIZE,
                total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
              )}
            />
          </GridBody>
        </MainPageGrid>
        {!isLoading && !entries.length && (
          <MainGridNoResultsStub textId="noSearchResults" showFiltersInfo={false} />
        )}
      </>
    </>
  );
};
